import {useAppSelector} from '@hooks/app';
import {IToken} from '@store/types';
import {noop} from '@utils/constants';
import {api} from '../api';

export const TRACK_MODELS = {
    rests: 'rests',
    warehouses: 'warehouses',
} as const;

export const TRACK_EVENTS = {
    /** Поиск товаров в каталоге остатков */
    productSearch: 'product_search',
    /** Открытие модалки остатков по товару */
    productRestsView: 'product_rests_view',
    /** Закрытие модалки остатков (крестик, неавторизованный) */
    productRestsClose: 'product_rests_close',
    /** Карточка поставщика в поиске складов */
    supplierSearchCard: 'supplier_search_card',
    /** Страница поставщика (/supplier/:id) */
    supplierPageView: 'supplier_page_view',
    /** Клик по типу товара в карточке поставщика */
    supplierProductTypeClick: 'supplier_product_type_click',
    /** Клик «Показать телефон» в модалке поставщика */
    supplierPhoneReveal: 'supplier_phone_reveal',
    /** Переход на сайт поставщика из модалки */
    supplierWebsiteClick: 'supplier_website_click',
    /** Клик по кнопке «Продать на» (RoboTyre) в хедере */
    robotyreClick: 'robotyre_click',
    /** Заход на сайт */
    siteVisit: 'site_visit',
    /** Успешная авторизация */
    userLogin: 'user_login',
    userNavigation: 'user_navigation',
    shareCopy: 'share_copy',
    shareTelegram: 'share_telegram',
    shareMax: 'share_max',
    shareEmail: 'share_email',
    onlineFittingOpen: 'online_fitting_open',
    onlineFittingGenerate: 'online_fitting_generate',
} as const;

export type TrackEvent = (typeof TRACK_EVENTS)[keyof typeof TRACK_EVENTS];

export type TrackParams = {
    event: TrackEvent;
    model?: keyof typeof TRACK_MODELS;
    model_id?: number | string | bigint;
    url?: string | null;
    ref_url?: string | null;
};

const STORAGE_KEYS = {
    lastActivityAt: 'track_last_activity_at',
    lastSiteVisitAt: 'track_last_site_visit_at',
} as const;

const INACTIVITY_MS = 3 * 60 * 60 * 1000;
const SITE_VISIT_MIN_INTERVAL_MS = 24 * 60 * 60 * 1000;

const getSessionId = (): string => {
    let sid = localStorage.getItem('session_id');
    if (!sid) {
        sid = crypto.randomUUID?.() ?? (Date.now().toString(36) + Math.random().toString(36).slice(2));
        localStorage.setItem('session_id', sid);
    }
    return sid;
};

const withoutAccessTokens = (value?: string | null): string | undefined => {
    if (!value) return undefined;
    try {
        const url = new URL(value, window.location.origin);
        url.searchParams.delete('share_token');
        url.searchParams.delete('invite_code');
        return url.toString();
    } catch {return undefined;}
};

export const trackEvent = (params: TrackParams, authToken?: IToken | null) => {
    const headers: Record<string, string> = {Accept: 'application/json'};
    if (authToken?.token) {
        headers.Authorization = `${authToken.token_type} ${authToken.token}`;
    }

    api.post('/track', {
        event: params.event,
        ...(params.model ? {model: params.model} : {}),
        model_id: params.model_id,
        url: withoutAccessTokens(params.url ?? window.location.href),
        ref_url: withoutAccessTokens(params.ref_url),
        session_id: getSessionId(),
    }, {headers}).catch(noop);
};

export const shouldTrackSiteVisit = (now = Date.now()): boolean => {
    const lastActivity = Number(localStorage.getItem(STORAGE_KEYS.lastActivityAt));
    const lastVisit = Number(localStorage.getItem(STORAGE_KEYS.lastSiteVisitAt));
    const inactiveLongEnough = !lastActivity || now - lastActivity >= INACTIVITY_MS;
    const visitAllowed = !lastVisit || now - lastVisit >= SITE_VISIT_MIN_INTERVAL_MS;
    return inactiveLongEnough && visitAllowed;
};

export const readStoredAuthToken = (): IToken | null => {
    const raw = localStorage.getItem('token');
    if (!raw) {
        return null;
    }

    try {
        const parsed = JSON.parse(raw) as IToken;

        return parsed?.token ? parsed : null;
    } catch {
        return null;
    }
};

export const resolveAuthToken = (token: unknown): IToken | null => {
    if (token && typeof token === 'object' && 'token' in token) {
        const parsed = token as IToken;

        if (typeof parsed.token === 'string' && parsed.token !== '') {
            return parsed;
        }
    }

    if (typeof token === 'string' && token !== '') {
        try {
            const parsed = JSON.parse(token) as IToken;

            if (parsed?.token) {
                return parsed;
            }
        } catch {
        }
    }

    return null;
};

export const trackSiteVisitIfNeeded = (authToken?: IToken | null): void => {
    if (!shouldTrackSiteVisit()) return;

    trackEvent({event: TRACK_EVENTS.siteVisit}, authToken ?? readStoredAuthToken());
    localStorage.setItem(STORAGE_KEYS.lastSiteVisitAt, String(Date.now()));
};

export const touchUserActivity = (): void => {
    localStorage.setItem(STORAGE_KEYS.lastActivityAt, String(Date.now()));
};

export const useTrack = () => {
    const token = useAppSelector(state => state.app.token);

    return (params: TrackParams) => trackEvent(params, resolveAuthToken(token));
};
